import { Component, h, Host, Prop, Event, EventEmitter, Build, Watch, Element } from '@stencil/core';
import Choices from 'choices.js';

import 'choicesjs-stencil/dist/choicesjsstencil';
import { Choice } from './rcm-select.types';
import { remCalc } from '../../style';

@Component({
  tag: 'rcm-select',
  styleUrl: 'rcm-select.scss',
  shadow: true,
})
export class RcmSelect {
  private choicesRef;

  @Prop() label: string;

  @Prop() required = false;

  @Prop() small = false;

  @Prop() sorted = false;

  @Prop() name: string;

  @Prop() hasSmallDropdown: boolean = false;

  @Prop() minWidth: string;

  @Prop({ attribute: 'options' }) tempChoices: string;

  @Prop({ mutable: true }) choices: Choice[];

  // use only if your placeholder is the first option of the choices array, does not work with sorted=true
  @Prop() labelAsPlaceholder: boolean = false;

  // don't emit change event when default option is set on load
  @Prop() noChangeOnLoad: boolean = false;

  @Event() changeEvent: EventEmitter;

  @Element() private element: HTMLRcmSelectElement;

  tempChoicesHandler(tempChoices: string) {
    if (tempChoices) {
      if (tempChoices.includes('{')) {
        this.choices = JSON.parse(tempChoices);
      } else {
        const parsedChoices = tempChoices ? tempChoices.split(',') : [];
        this.choices = parsedChoices?.map((choice) => ({
          value: choice.trim(),
        }));
      }
    }
  }

  @Watch('tempChoices')
  updateChoices() {
    this.tempChoicesHandler(this.tempChoices);
  }

  componentWillLoad() {
    this.tempChoicesHandler(this.tempChoices);
    if (Build.isBrowser) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).Choices = Choices;
    }
  }

  async componentDidLoad() {
    // choices.js pre-select a value, so the form state needs to be updated
    if (Build.isBrowser) {
      // this is a "hack" to use a selected placeholder
      // it will render with the first option but not show it as a option when clicking the dropdown
      if (this.labelAsPlaceholder) {
        // remove the label if it's under the choiches
        const choicesForRendering: Choice[] = this.choices.filter((choice) => choice.value !== this.label);
        this.choicesRef.setChoices(choicesForRendering, 'value', 'label', true);
      }

      this.getSelectedValue(true);
    }
  }

  openDropDown() {
    const listbox = this.element.shadowRoot.querySelectorAll<HTMLElement>('.choices__list')[2];

    if (window.innerHeight < 500) {
      listbox.style.setProperty('max-height', '140px');
    } else {
      listbox.style.setProperty('max-height', '300px');
    }

    this.choicesRef?.showDropdown(false);
  }

  async getSelectedValue(isFirstMount?: boolean) {
    if (this.choicesRef) {
      const selectedValue = await this.choicesRef.getValue();
      if (!this.noChangeOnLoad || !isFirstMount) {
        this.changeEvent.emit({ value: selectedValue?.value, name: this.name, ref: this.choicesRef });
      }
    }
  }

  render() {
    return (
      <Host>
        {this.label && !this.labelAsPlaceholder ? (
          <label aria-label="select" class="label">
            {this.label}
            {this.required ? ' *' : ''}
          </label>
        ) : (
          ''
        )}
        <div class={this.small ? 'input_wrapper input_wrapper--small' : 'input_wrapper'} style={{ minWidth: this.minWidth ?? `${remCalc(120)}rem` }}>
          {Build.isBrowser && (
            <choicesjs-stencil
              choices={this.choices}
              aria-label="select"
              name="projects"
              type="single"
              key={'label'}
              itemScope={true}
              searchEnabled={false}
              renderChoiceLimit={-1}
              position={'auto'}
              renderSelectedChoices={'auto'}
              silent={false}
              shouldSort={this.sorted}
              addItemText={(value) => `Press Enter to add <b>"${value}"</b>`}
              maxItemText={(maxItemCount) => `Only ${maxItemCount} values can be added`}
              valueComparer={(value1, value2) => value1 === value2}
              addItemFilter={(value) => /^[^\s]+$/.test(value)}
              removeItems={true}
              ref={(el) => {
                this.choicesRef = el;
              }}
              onClick={() => this.openDropDown()}
              onChange={() => this.getSelectedValue()}
              class={{ 'small-dropdown': this.hasSmallDropdown }}
            ></choicesjs-stencil>
          )}
          <rcm-icon icon="chevron-down"></rcm-icon>
        </div>
      </Host>
    );
  }
}
